'use client';

import { type FC, useContext, useMemo, useState } from 'react';
import { Button, Alert, Dialog, Text } from '@/_components';
import type { UploadedFileProps } from './UploadedFile.types';
import { getShortUploadName } from '@/_utils/answers';
import { UserContext } from '@/_contexts/UserProvider';
import { autoSplitDocument } from '@/_api/admin-documents';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { DialogContent } from '@/_components/Dialog/_components/DialogContent';
import { DialogFooter } from '@/_components/Dialog/_components/DialogFooter';
import { DraggableDocumentItem } from '../DraggableDocumentItem';

export const UploadedFile: FC<UploadedFileProps> = ({
  documents,
  mode,
  isLoading,
  onPreviewDocument,
  onRemoveDocument,
  questionnaire,
  shouldAddRecognitionAiPill,
  uneditable,
  onSplitDocument,
}) => {
  const { user, token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isSplitLoading, setIsSplitLoading] = useState(false);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);
  const [documentToRemove, setDocumentToRemove] = useState<string | null>(null);

  const hasSplitDocument: boolean = useMemo(() => {
    const isAnyFromSplit = questionnaire.extracted_documents?.some(
      (document) => !!document.parent_document_id,
    );
    return isAnyFromSplit || false;
  }, [questionnaire.extracted_documents]);

  const splitDocument = async (document: any) => {
    try {
      const extractedDocument = questionnaire.extracted_documents?.find(
        (ed: any) => ed.source.split('/').pop() === document.split('/').pop(),
      );

      setIsSplitLoading(true);
      setIsSplitDialogOpen(true);
      await autoSplitDocument({
        token,
        extractedDocumentId: extractedDocument?.id!,
        companyId: questionnaire.company_id!,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSplitLoading(false);
      onSplitDocument();
    }
  };

  const getIsDocumentProcessing = (document: string) => {
    return (
      questionnaire.extracted_documents?.find(
        (ed: any) => ed.source.split('/').pop() === document.split('/').pop(),
      )?.status === 'processing'
    );
  };

  return (
    <>
      {isSplitDialogOpen && (
        <Dialog
          isOpen={isSplitDialogOpen}
          onClose={() => setIsSplitDialogOpen(false)}
        >
          <DialogContent>
            <Alert type="info" icon="info">
              <Text>Document has been sent for splitting.</Text>
            </Alert>
          </DialogContent>
          <DialogFooter>
            <Button
              intent="primary"
              value="Close"
              onClick={() => setIsSplitDialogOpen(false)}
            />
          </DialogFooter>
        </Dialog>
      )}
      {!!documents.length && hasSplitDocument && !user?.is_advisor && (
        <Alert type="success" icon="check-circle">
          Marked as completed by firm. Use the upload button below to provide
          additional documents.
        </Alert>
      )}

      {documents?.map?.((document: any, _index: number) => {
        const isDisabled = isLoading || uneditable;
        const isRemoving = documentToRemove === document;

        const onPreview = () => onPreviewDocument(document);
        const onRemove = async () => {
          try {
            setDocumentToRemove(document);
            await onRemoveDocument(document);
          } catch (error) {
            console.error(error);
          } finally {
            setDocumentToRemove(null);
          }
        };

        const documentName: string = getShortUploadName(document);
        const isDocumentProcessing = getIsDocumentProcessing(document);
        let isRecognizedByAi = false;
        let isProcessing = false;
        let isFromSplitDocument: boolean = false;
        let documentDescription: string | undefined;

        // Get document description if enabled
        if (preferences?.append_description_to_filename_enabled) {
          const extractedDocument: any =
            questionnaire?.extracted_documents?.find(
              (ed: any) =>
                ed.source.split('/').pop() === document.split('/').pop(),
            );
          const description = extractedDocument?.description;
          if (description && description.toLowerCase() !== 'unknown') {
            documentDescription = description;
          }
        }

        // Check if document is from split regardless of shouldAddRecognitionAiPill
        const extractedDocumentForSplit: any =
          questionnaire?.extracted_documents?.find(
            (ed: any) =>
              ed.source.split('/').pop() === document.split('/').pop(),
          );

        if (!!extractedDocumentForSplit?.parent_document_id) {
          isFromSplitDocument = true;
        }

        if (shouldAddRecognitionAiPill) {
          const extractedDocument: any =
            questionnaire?.extracted_documents?.find(
              (ed: any) =>
                ed.source.split('/').pop() === document.split('/').pop(),
            );

          if (extractedDocument?.categorized_by_ai === true) {
            isRecognizedByAi = true;
          } else if (extractedDocument?.status === 'processing') {
            isProcessing = true;
          }
        }

        let infoPillText: string | undefined;

        if (shouldAddRecognitionAiPill) {
          if (isRecognizedByAi) {
            infoPillText = 'Categorized by AI';
          } else if (isProcessing) {
            infoPillText = 'Processing';
          }
        }

        return (
          <DraggableDocumentItem
            key={document}
            document={document}
            documentName={documentName}
            documentDescription={documentDescription}
            infoPillText={infoPillText}
            isDisabled={isDisabled || false}
            isRemoving={isRemoving}
            isProcessing={isProcessing}
            isDocumentProcessing={isDocumentProcessing}
            isSplitLoading={isSplitLoading}
            isFromSplitDocument={Boolean(isFromSplitDocument)}
            onPreview={onPreview}
            onRemove={onRemove}
            splitDocument={splitDocument}
            mode={mode}
            user={user}
            preferences={preferences}
          />
        );
      })}
    </>
  );
};
